import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import startCase from "lodash.startcase";
import { badgesSelector } from "../../../../../redux/eventEdition/selectors/eventEdition.selectors";
import { productsSelector } from "../../../../../redux/magento/selectors/magento.selectors";
import { organisationsSelector } from "../../../../../redux/organisations/selectors/organisation.selectors";
import { IPersonBadge, IProduct, Nullable, Organisation } from "../../../../../common/types";
import { BadgePaymentStatus } from "../../../../../common/enums";
import { CompanyBadgesTableRows } from "../../../enums";
import { CompanyBadgesTableRow } from "../../../types";
import { CompanySearchResult } from "../../../../search/types";
import { useCompanyBadgesFilters } from "./useCompanyBadgesFilters";
import PublicApiService from "services/PublicApiService";

type Props = {
  selectedCompany?: CompanySearchResult;
};

type CompanyData = IPersonBadge["organisation"] & {
  id: string;
  parentId: Nullable<string>;
};

export type MshOrganisation = Organisation & {
  displayName: string;
};

export const useGetCompanyBadges = ({ selectedCompany }: Props) => {
  const { t } = useTranslation("common");
  const organisations = useSelector(organisationsSelector);
  const badges: IPersonBadge[] = useSelector(badgesSelector);
  const products: IProduct[] = useSelector(productsSelector);
  const currentCompanyKeyRef = useRef<string | null>(null);
  const [tableData, setTableData] = useState<CompanyBadgesTableRow[]>([]);
  const [companyData, setCompanyData] = useState<Nullable<CompanyData>>(null);
  const [leadManagerCode, setLeadManagerCode] = useState<string | null>(null);
  const initialTableData = useRef<CompanyBadgesTableRow[]>([]);
  const [mshOrganisations, setMshOrganisations] = useState<Nullable<MshOrganisation>>(null);

  useEffect(() => {
    if (!companyData) {
      setMshOrganisations(null);
      return;
    }
    const parentOrganisation = organisations.find(organisation => organisation.id === companyData.parentId);
    if (!parentOrganisation) {
      setMshOrganisations(null);
      return;
    }

    setMshOrganisations({
      ...parentOrganisation,
      displayName: parentOrganisation.companyName ?? ""
    });
  }, [companyData, organisations]);

  const { filterSetters, filterGetters, config, filterTableData } = useCompanyBadgesFilters({
    tableData: initialTableData.current,
    updateTableData: setTableData,
    products
  });

  const fetchLeadManagerCode = (organisationId: Nullable<string>) => {
    if (!organisationId) {
      setLeadManagerCode(null);
      return;
    }

    PublicApiService.getLeadManagerCode(organisationId)
      .then((res: { leadManagerCode?: Nullable<string> }) => {
        setLeadManagerCode(res?.leadManagerCode ?? null);
      })
      .catch(() => setLeadManagerCode(null));
  };

  const defineCompanyData = (companyResult: CompanySearchResult) => {
    const { organisation } = companyResult;

    const name = organisation.displayName;
    const orgIdFromSearch = organisation.participatingOrganisationId;
    const organisationFromStore = organisations.find(org => org.id === orgIdFromSearch || org.companyName === name);
    const standIds = organisationFromStore?.stands.map(stand => stand.name) || [];
    setTimeout(() => (document.title = `${name} | ${t("headTitles.defaultTitle")}`), 250);

    let companyBadge: IPersonBadge | undefined;

    if (orgIdFromSearch) {
      companyBadge = badges.find(badge => badge.organisation.participatingOrganisationId === orgIdFromSearch);
    }

    if (!companyBadge) {
      companyBadge = badges.find(badge => badge.organisation.displayName === name);
    }

    if (!companyBadge) {
      const organisationFromStore = organisations.find(org => org.id === orgIdFromSearch || org.companyName === name);
      const organisationdId = organisationFromStore?.id ?? orgIdFromSearch ?? "";
      const standIds = organisationFromStore?.stands.map(stand => stand.name) || [];
      setCompanyData({
        ...({} as IPersonBadge["organisation"]),
        id: organisationdId,
        parentId: organisationFromStore?.parentId ?? null,
        displayName: name,
        participatingOrganisationId: organisationFromStore?.id ?? null,
        standIds: standIds
      });
      fetchLeadManagerCode(organisationdId);
      setTableData([]);
      return;
    }

    const participatingOrganisationId =
      organisationFromStore?.id ?? orgIdFromSearch ?? companyBadge!.organisation.participatingOrganisationId ?? null;

    if (participatingOrganisationId) {
      fetchLeadManagerCode(participatingOrganisationId);
    } else {
      setLeadManagerCode(null);
    }

    setCompanyData({
      ...companyBadge!.organisation,
      displayName: name,
      participatingOrganisationId,
      id: organisationFromStore?.id ?? companyBadge!.organisation.participatingOrganisationId ?? "",
      parentId: organisationFromStore?.parentId ?? companyBadge!.parentId ?? null,
      standIds: standIds
    });

    defineTableData({
      ...companyResult,
      organisation: {
        ...companyResult.organisation,
        participatingOrganisationId
      }
    });
  };

  const getCompanyLogo = (company: IPersonBadge["organisation"]) => {
    if (company.participatingOrganisationId) {
      return null;
    }
    const foundOrganisation = organisations.find(p => p.id === company.participatingOrganisationId);
    if (!foundOrganisation?.logoUrl) {
      return null;
    }
    return foundOrganisation.logoUrl;
  };

  const getCompanyBadgesBasedOnCurrentTable = (badgesFromStore: IPersonBadge[]) => {
    let tableBadges: IPersonBadge[] = [];
    tableData.forEach(data => {
      const badge = data[CompanyBadgesTableRows.BADGE] as IPersonBadge;
      tableBadges.push(badge);
    });

    badgesFromStore.forEach(storeBadge => {
      const badgeFromTable = tableBadges.find(tableBadge => tableBadge.id === storeBadge.id);
      // if badge is not displayed on UI
      if (!badgeFromTable) {
        tableBadges.push(storeBadge);
        return;
      }

      // if badge is present in the table, but status was changed => update the UI
      if (storeBadge.status !== badgeFromTable.status) {
        tableBadges = tableBadges.map(tableBadge => {
          return tableBadge.id === badgeFromTable.id ? storeBadge : tableBadge;
        });
      }
    });
    return tableBadges;
  };

  const getCompanyBadges = (company: CompanySearchResult) => {
    const name = company.organisation.displayName;
    const participatingOrganisationId = company.organisation.participatingOrganisationId;

    const companyKey = participatingOrganisationId || name;

    let badgesFromStore: IPersonBadge[];

    if (participatingOrganisationId) {
      badgesFromStore = badges.filter(badge => badge.organisation.participatingOrganisationId === participatingOrganisationId);
    } else {
      badgesFromStore = badges.filter(badge => badge.organisation.displayName === name);
    }

    if (currentCompanyKeyRef.current !== companyKey) {
      currentCompanyKeyRef.current = companyKey;
      return badgesFromStore;
    }

    if (tableData.length > 0) {
      return getCompanyBadgesBasedOnCurrentTable(badgesFromStore);
    }
    return badgesFromStore;
  };

  const defineTableData = (company: CompanySearchResult) => {
    const updatedTableData = prepareCleanTableData(company);
    updateTableData(updatedTableData);
  };

  const prepareCleanTableData = (company: CompanySearchResult) => {
    const companyBadges = getCompanyBadges(company);

    return companyBadges.map(badge => {
      const { contact, status, customerType, paymentStatus } = badge;
      const product = products.find(p => p.sku === badge.productCode);
      if (!product) {
        return null;
      }
      return [
        `${contact.firstName} ${contact.lastName}`,
        status.toLowerCase(),
        startCase(t(`customerTypesTitles.${customerType}`)),
        product.name,
        paymentStatus,
        badge
      ];
    }) as CompanyBadgesTableRow[];
  };

  const updateTableData = (updatedTableData: CompanyBadgesTableRow[]) => {
    const data = updatedTableData
      .filter(Boolean)
      .sort((a, b) => (a[0] as string).toLowerCase().localeCompare((b[0] as string).toLowerCase())) as string[][];

    setTableData(data);
    filterTableData(data);
    config.configureOptionsForFilters(data);
    initialTableData.current = data;
  };

  const getBadgesFromTableData = () => {
    return tableData.map(row => row[CompanyBadgesTableRows.BADGE]) as IPersonBadge[];
  };

  const updateBadgesPaymentStatuses = (paymentStatus: BadgePaymentStatus, badgesToChange: IPersonBadge[], company: CompanySearchResult) => {
    const preparedTableData = prepareCleanTableData(company);
    for (const badge of badgesToChange) {
      const foundRow = preparedTableData.find(row => (row[CompanyBadgesTableRows.BADGE]! as IPersonBadge).id === badge.id);
      if (foundRow) {
        preparedTableData[preparedTableData.indexOf(foundRow)]![CompanyBadgesTableRows.PAYMENT_STATUS] = paymentStatus;
        (preparedTableData[preparedTableData.indexOf(foundRow)]![CompanyBadgesTableRows.BADGE] as IPersonBadge).paymentStatus =
          paymentStatus;
      }
    }

    updateTableData(preparedTableData);
  };

  useEffect(() => {
    if (selectedCompany) {
      defineTableData(selectedCompany);
    }
  }, [
    selectedCompany,
    badges,
    filterGetters.selectedBadgeStatus,
    filterGetters.selectedCustomerType,
    filterGetters.selectedProduct,
    filterGetters.selectedBadgePaymentStatus
  ]);

  const resetTableData = (updatedTableData: CompanyBadgesTableRow[]) => {
    setTableData([...updatedTableData]);
    filterTableData(updatedTableData);
  };

  return {
    defineCompanyData,
    resetTableData,
    tableData,
    getCompanyLogo,
    companyData,
    leadManagerCode,
    getBadgesFromTableData,
    filterSetters,
    filterGetters,
    updateBadgesPaymentStatuses,
    mshOrganisations
  };
};
